import {
  use,
  createContext,
  useRef,
  useMemo,
  ReactNode,
  PropsWithChildren,
  ComponentProps,
  Context,
  useEffect,
  RefObject,
} from "react"
import { createStore, StateCreator, StoreApi, useStore } from "zustand"
import {
  useIsMutating,
  UseMutateFunction,
  useMutation,
} from "@tanstack/react-query"
import { Button } from "@repo/ui"
import { useTranslation } from "react-i18next"
import { useOnClickOutside } from "usehooks-ts"
import {
  ClipboardParsedItem,
  ClipboardParseResult,
  ClipboardParseValidationType,
  parseExcelData,
  readClipboardData,
} from "@/hooks/use-parse-excel-clipboard-input.ts"
import { cn } from "@/lib/utils"
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu"
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip"
import { TableCell } from "@/components/ui/table.tsx"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog.tsx"
import { RowWithInputTypeContext } from "./popover-context"
import { Show } from "../show"
import { PeriodTypeQuery, StandardInputType } from "@/types"
import { PageContext } from "@/lib/context/page"
import { PeriodTableCell } from "./table-cell"

// ***** NESTED PROP TYPE AND PATCH HELPERS ******** //

/**
 * Type representing a path to a nested property in an object using dot notation
 * Examples: "id", "user.name", "address.city.zipCode"
 */
type NestedKeyOf<T> = {
  [K in keyof T & (string | number)]: T[K] extends object
    ? `${K}` | `${K}.${NestedKeyOf<T[K]>}`
    : `${K}`
}[keyof T & (string | number)]

/**
 * Creates a patched copy of an object by setting a value at the specified nested path
 * @param obj The source object to create a patched copy from
 * @param path The dot notation path to the property to modify (e.g., "a.b.c")
 * @param value The new value to set at the specified path
 * @returns A new object with the updated property (original object is not modified)
 */

function getPatchedObjectByNestedProps<T extends object, V>(
  obj: T,
  path: string,
  value: V
): T {
  if (!path) {
    return obj
  }

  // Create a shallow copy of the object
  const result = { ...obj }

  // Split the path into parts
  const keys = path.split(`.`)

  // Reference to the current level in the result object
  let current: Record<string, any> = result as Record<string, any>

  // Navigate through the path except the last key
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]

    // If the key doesn't exist or is not an object, create an empty object
    if (
      !(key in current) ||
      current[key] === null ||
      typeof current[key] !== `object`
    ) {
      current[key] = {}
    } else {
      // Create a shallow copy of the nested object to avoid modifying the original
      current[key] = { ...current[key] }
    }

    // Move to the next level
    current = current[key] as typeof current
  }

  // Set the value at the final key
  const lastKey = keys[keys.length - 1]
  current[lastKey as keyof typeof current] = value

  return result
}

// **** PASTE ACTION TYPES *** //

const PASTE_ACTION_TYPES = {
  PASTE_ALL: `PASTE_ALL`,
  PASTE_PRESERVE_EMPTY: `PASTE_PRESERVE_EMPTY`,
  PASTE_SKIP_EMPTY: `PASTE_SKIP_EMPTY`,
} as const

type PasteActionType =
  (typeof PASTE_ACTION_TYPES)[keyof typeof PASTE_ACTION_TYPES]

// ***** PENDING PASTE ERROR TYPES **** //

const PENDING_PASTE_ERROR = {
  INVALID_CHARS: `invalid_chars`,
  LIMIT_OVERFLOW: `limit_overflow`,
} as const

type PendingPasteErrorType =
  (typeof PENDING_PASTE_ERROR)[keyof typeof PENDING_PASTE_ERROR]

// **** ACTION TYPES **** //
type Action<T extends string, P extends object> = {
  type: T
  payload: P
}

const PARENT_ROW_ACTION_TYPES = {
  OUTER_CLICK: `parentRow/outerClick`,
} as const

type ParentRowActionType =
  (typeof PARENT_ROW_ACTION_TYPES)[keyof typeof PARENT_ROW_ACTION_TYPES]
type ParentRowActionAction = Action<
  ParentRowActionType,
  {
    cellIndex: number
  }
>

const CELL_FEEDBACK_STATE_ACTION_TYPES = {
  SET_ERROR_FEEDBACK: `cellFeedbackState/setError`,
  SET_SUCCESS_FEEDBACK: `cellFeedbackState/setSuccess`,
} as const

type CellFeedbackStateActionType =
  (typeof CELL_FEEDBACK_STATE_ACTION_TYPES)[keyof typeof CELL_FEEDBACK_STATE_ACTION_TYPES]

type CellFeedbackStateAction = Action<
  CellFeedbackStateActionType,
  {
    mutationStatus: {
      message?: string
      cellIndex: number
      pasteIndex: number
    }
  }
>

const CELL_ACTION_TYPES = {
  SINGLE_CLICK: `cell/singleClick`,
  OUTER_CLICK: `cell/outerClick`,
} as const

type CellActionType = (typeof CELL_ACTION_TYPES)[keyof typeof CELL_ACTION_TYPES]
type CellActionAction = Action<
  CellActionType,
  {
    cellIndex: number
  }
>

const CURRENT_DATA_ACTION_TYPES = {
  SET: `currentData/set`,
} as const

type CurrentDataActionTypes<DataItem extends object> = Action<
  (typeof CURRENT_DATA_ACTION_TYPES)[keyof typeof CURRENT_DATA_ACTION_TYPES],
  {
    currentData: DataItem[]
  }
>

const CONTEXT_MENU_COMMAND_PREVIEW_ACTION_TYPES = {
  SET_PREVIEW: `contextMenuPreview/setPreview`,
} as const

type ContextMenuCommandPreviewAction<DataItem extends object> = Action<
  (typeof CONTEXT_MENU_COMMAND_PREVIEW_ACTION_TYPES)[keyof typeof CONTEXT_MENU_COMMAND_PREVIEW_ACTION_TYPES],
  {
    currentData: DataItem[]
    mutateDataKey: keyof DataItem
  }
>

const CLIPBOARD_STATE_ACTION_TYPES = {
  UPDATE_CLIPBOARD_INFO: `clipboard/updateInfo`,
} as const

type UpdateClipboardInfoAction = Action<
  (typeof CLIPBOARD_STATE_ACTION_TYPES)[keyof typeof CLIPBOARD_STATE_ACTION_TYPES],
  {
    original: string
    data: string[]
    count: number
  }
>

const MENU_STATE_ACTION_TYPES = {
  SET_CONTEXT_MENU_OPEN: `contextMenu/setOpen`,
} as const

type MenuStateAction = Action<
  (typeof MENU_STATE_ACTION_TYPES)[keyof typeof MENU_STATE_ACTION_TYPES],
  {
    isOpen: boolean
    triggerCellIndex: number
  }
>

const PASTE_FEEDBACK_ACTION_TYPES = {
  PASTE_SUCCESS: `paste/success`,
  PASTE_ERRORED: `paste/errored`,
  UNDO_PASTE_SUCCESS: `paste/undoSuccess`,
  PASTE_CANCEL: `paste/cancel`,
} as const

type PasteFeedbackActionType =
  (typeof PASTE_FEEDBACK_ACTION_TYPES)[keyof typeof PASTE_FEEDBACK_ACTION_TYPES]

type PasteFeedbackActionAction<
  DataItem extends object,
  T extends PasteFeedbackActionType = PasteFeedbackActionType,
> = Action<
  T,
  T extends
    | typeof PASTE_FEEDBACK_ACTION_TYPES.PASTE_SUCCESS
    | typeof PASTE_FEEDBACK_ACTION_TYPES.PASTE_ERRORED
    ? {
        upsertData: (DataItem & { itemIndex: number })[]
        prevData: DataItem[] | null
      }
    : {}
>

const INVALID_PENDING_PASTE_ACTION_TYPES = {
  SET_PENDING_PASTE_ERROR: `invalidPaste/setPendingPasteError`,
} as const

type PendingErroredParsedClipboardInputState = {
  triggerCellIndex: number
  cps: ClipboardParseResult
  errorCode: PendingPasteErrorType
}

type InvalidPendingPasteAction = Action<
  (typeof INVALID_PENDING_PASTE_ACTION_TYPES)[keyof typeof INVALID_PENDING_PASTE_ACTION_TYPES],
  {
    cps: ClipboardParseResult
    triggerCellIndex: number
    errorCode: (typeof PENDING_PASTE_ERROR)[keyof typeof PENDING_PASTE_ERROR]
  }
>
type ReducerActions<DataItem extends object = object> =
  | MenuStateAction
  | UpdateClipboardInfoAction
  | InvalidPendingPasteAction
  | PasteFeedbackActionAction<DataItem>
  | ContextMenuCommandPreviewAction<DataItem>
  | CurrentDataActionTypes<DataItem>
  | CellActionAction
  | ParentRowActionAction
  | CellFeedbackStateAction

type RowState<DataItem extends object = object> = {
  dataValidationType: ClipboardParseValidationType
  clipboardInfo: {
    original: string
    data: string[]
    count: number
  } | null
  pendingErroredParsedClipboardInputState: PendingErroredParsedClipboardInputState | null
  isContextMenuOpen: boolean
  contextMenuActionPreview: {
    pasteAll: string
    pastePreserveEmpty: string
    pasteSkipEmpty: string
  }
  mutationCellsStatus: {
    status: "pending" | "ok" | "error"
    message: string
    cellIndex: number
  }[]
  contextMenuTriggerCellIndex: number
  isFeedbackDialogOpen: boolean
  upsertData: (DataItem & { itemIndex: number })[]
  prevData: DataItem[] | null
  currentData: DataItem[]
  dispatch: (action: ReducerActions<DataItem> | ThunkLikeAction) => void
}

type ThunkLikeAction<DataItem extends object = object> = (options: {
  dispatch: (action: ReducerActions<DataItem> | ThunkLikeActions) => void
  getState: () => RowState<DataItem>
  mutateFn: UseMutateFunction<
    { status: "ok" | "with-errors" },
    Error,
    {
      upsertData: (DataItem & { itemIndex: number })[]
      prevData: DataItem[] | null
    },
    unknown
  >
  mutateDataKey: NestedKeyOf<DataItem>
}) => Promise<void>

type ThunkLikeActionCreator<
  P extends object = object,
  DataItem extends object = object,
> = (payload: P) => ThunkLikeAction<DataItem>

const checkClipboardInput: ThunkLikeActionCreator =
  () =>
  async ({ dispatch }) => {
    const pasteableData = await readClipboardData()
    const rows = pasteableData.split(/\r\n|\r|\n/) // Handle different line breaks
    const row = rows[0]?.split(`\t`) ?? []
    dispatch({
      type: CLIPBOARD_STATE_ACTION_TYPES.UPDATE_CLIPBOARD_INFO,
      payload: {
        original: pasteableData,
        data: row,
        count: row.length,
      },
    })
  }

const openContextMenu: ThunkLikeActionCreator<{
  triggerCellIndex: number
}> =
  ({ triggerCellIndex }) =>
  async ({ dispatch, mutateDataKey, getState }) => {
    const { currentData } = getState()
    const pasteableData = await readClipboardData()
    const rows = pasteableData.split(/\r\n|\r|\n/) // Handle different line breaks
    const row = rows[0]?.split(`\t`) ?? []
    dispatch({
      type: CLIPBOARD_STATE_ACTION_TYPES.UPDATE_CLIPBOARD_INFO,
      payload: {
        original: pasteableData,
        data: row,
        count: row.length,
      },
    })
    dispatch({
      type: MENU_STATE_ACTION_TYPES.SET_CONTEXT_MENU_OPEN,
      payload: {
        isOpen: true,
        triggerCellIndex,
      },
    })
    dispatch({
      type: CONTEXT_MENU_COMMAND_PREVIEW_ACTION_TYPES.SET_PREVIEW,
      payload: {
        currentData,
        mutateDataKey,
      },
    })
  }

const applyPaste: ThunkLikeActionCreator<{
  validPasted: ClipboardParseResult["data"]
  type: PasteActionType
}> =
  ({ validPasted, type }) =>
  async ({ mutateFn, dispatch, getState, mutateDataKey }) => {
    const { currentData } = getState()
    const triggerCellIndex = getState().contextMenuTriggerCellIndex

    if (triggerCellIndex === -1) {
      throw new Error(`No trigger cell index`)
    }

    const typePatchFn = {
      [PASTE_ACTION_TYPES.PASTE_ALL]: (parsedItem) =>
        +parsedItem.validatedValue,
      // passing empty object implies preserving data
      [PASTE_ACTION_TYPES.PASTE_PRESERVE_EMPTY]: (parsedItem) =>
        parsedItem.validatedValue !== `` ? +parsedItem.validatedValue : 0,
      // empty values should not be passed from validPasted in the first place so the
      // empty check is just a safety net
      [PASTE_ACTION_TYPES.PASTE_SKIP_EMPTY]: (parsedItem) =>
        parsedItem.validatedValue !== `` ? +parsedItem.validatedValue : 0,
    } satisfies Record<
      typeof type,
      (
        parsedItem: ClipboardParsedItem,
        patchIndex: number,
        targetIndex: number
      ) => number
    >
    mutateFn(
      {
        upsertData: validPasted.map((vp, vpIdx) => {
          const itemIndex = triggerCellIndex + vpIdx
          const found = currentData.find((_, index) => index === itemIndex)
          if (!found) {
            throw new Error(`Item not found`)
          }
          const patch = typePatchFn[type](vp)
          return {
            ...found,
            ...getPatchedObjectByNestedProps(
              structuredClone(found),
              mutateDataKey,
              patch
            ),
            itemIndex,
          }
        }),
        prevData: currentData,
      },
      {
        onSuccess: (resolved, variables) => {
          dispatch({
            type:
              resolved.status === `ok`
                ? PASTE_FEEDBACK_ACTION_TYPES.PASTE_SUCCESS
                : PASTE_FEEDBACK_ACTION_TYPES.PASTE_ERRORED,
            payload: {
              upsertData: variables.upsertData,
              prevData: variables.prevData,
            },
          })
        },
        onError: (e) => {
          // we should never be here
          // all errors should be handled in the mutation
          // and invalidation of current data server state should always happen
          console.error(`Error while applying paste`, e)
        },
      }
    )
  }

const tryPasteClipboardInput: ThunkLikeActionCreator<{
  type: PasteActionType
  triggerCellIndex: number
}> =
  ({ type, triggerCellIndex }) =>
  async ({ dispatch, getState }) => {
    const { currentData } = getState()
    const pastedData = await readClipboardData()
    const validate = getState().dataValidationType
    const clipboardParseResult = parseExcelData(pastedData, {
      validate,
    })

    let errorCode = ``
    if (clipboardParseResult.errors.length) {
      errorCode = PENDING_PASTE_ERROR.INVALID_CHARS
    } else if (
      clipboardParseResult.data.length >
      currentData.length - triggerCellIndex
    ) {
      errorCode = PENDING_PASTE_ERROR.LIMIT_OVERFLOW
    }
    if (errorCode) {
      dispatch({
        type: INVALID_PENDING_PASTE_ACTION_TYPES.SET_PENDING_PASTE_ERROR,
        payload: {
          cps: clipboardParseResult,
          triggerCellIndex,
          errorCode: errorCode as PendingPasteErrorType,
        },
      })
      return
    }
    const typePrepareFn: Record<
      typeof type,
      (cps: ClipboardParseResult) => ClipboardParsedItem[]
    > = {
      [PASTE_ACTION_TYPES.PASTE_ALL]: (cps) => cps.data,
      [PASTE_ACTION_TYPES.PASTE_PRESERVE_EMPTY]: (cps) => cps.data,
      [PASTE_ACTION_TYPES.PASTE_SKIP_EMPTY]: (cps) =>
        cps.data.filter((item) => item.validatedValue !== ``),
    }

    dispatch(
      applyPaste({
        validPasted: typePrepareFn[type](clipboardParseResult),
        type,
      })
    )
  }

const undoPaste: ThunkLikeActionCreator =
  () =>
  async ({ dispatch, mutateFn, getState }) => {
    const {
      prevData: fullRowDataBeforePaste,
      contextMenuTriggerCellIndex,
      upsertData: pastedData,
    } = getState()
    if (!fullRowDataBeforePaste) {
      throw new Error(`No undo data`)
    }
    // this way we ensure to just make api calls for involved cells
    const restoreDataCells = fullRowDataBeforePaste.filter(
      (_, i) =>
        i >= contextMenuTriggerCellIndex &&
        i < contextMenuTriggerCellIndex + pastedData.length
    )

    mutateFn(
      {
        upsertData: restoreDataCells.map((item, index) => ({
          ...item,
          itemIndex: contextMenuTriggerCellIndex + index,
        })),
        prevData: null,
      },
      {
        onSuccess: () => {
          dispatch({
            type: PASTE_FEEDBACK_ACTION_TYPES.UNDO_PASTE_SUCCESS,
            payload: {},
          })
        },
        onError: () => {
          console.error(`Error while undoing paste`)
        },
      }
    )
  }

const pasteErroredClipboardInput: ThunkLikeActionCreator =
  () =>
  async ({ dispatch, getState }) => {
    const { currentData } = getState()
    const { pendingErroredParsedClipboardInputState } = getState()
    if (!pendingErroredParsedClipboardInputState) {
      throw new Error(`No errored pending clipboard input`)
    }
    const { errorCode, cps, triggerCellIndex } =
      pendingErroredParsedClipboardInputState

    let errorFixed = cps.data
    if (errorCode === PENDING_PASTE_ERROR.LIMIT_OVERFLOW) {
      errorFixed = errorFixed.filter(
        (_, index) => index < currentData.length - triggerCellIndex
      )
    }

    const prepared = errorFixed.map((item) =>
      item.error
        ? {
            ...item,
            validatedValue: ``,
          }
        : item
    )
    dispatch(
      applyPaste({
        validPasted: prepared,
        type: PASTE_ACTION_TYPES.PASTE_ALL,
      })
    )
    dispatch({
      type: MENU_STATE_ACTION_TYPES.SET_CONTEXT_MENU_OPEN,
      payload: {
        isOpen: false,
        triggerCellIndex,
      },
    })
  }
type ThunkLikeActionCreators =
  | typeof checkClipboardInput
  | typeof tryPasteClipboardInput
  | typeof applyPaste
  | typeof pasteErroredClipboardInput
  | typeof undoPaste
  | typeof openContextMenu
type ThunkLikeActions =
  | ReturnType<typeof checkClipboardInput>
  | ReturnType<typeof tryPasteClipboardInput>
  | ReturnType<typeof applyPaste>
  | ReturnType<typeof pasteErroredClipboardInput>
  | ReturnType<typeof undoPaste>
  | ReturnType<typeof openContextMenu>

type ActionableRowContextType<DataItem extends object = object> = {
  store: StoreApi<RowState<DataItem>>
  mutationKey: readonly unknown[] | undefined
  dataLength: number
  rowType: StandardInputType
  queryPeriod: PeriodTypeQuery
}

const ActionableRowContext = createContext<ActionableRowContextType | null>(
  null
)

const reducer = <DataItem extends object>(
  state: RowState<DataItem>,
  action: ReducerActions<DataItem>
) => {
  switch (action.type) {
    case CELL_FEEDBACK_STATE_ACTION_TYPES.SET_SUCCESS_FEEDBACK: {
      const element = {
        status: `ok`,
        message: action.payload.mutationStatus.message ?? ``,
        cellIndex: action.payload.mutationStatus.cellIndex,
      } as const

      return {
        ...state,
        mutationCellsStatus:
          action.payload.mutationStatus.pasteIndex === 0
            ? [element]
            : state.mutationCellsStatus.concat([element]),
      }
    }
    case CELL_FEEDBACK_STATE_ACTION_TYPES.SET_ERROR_FEEDBACK: {
      const element = {
        status: `error`,
        message: action.payload.mutationStatus.message ?? ``,
        cellIndex: action.payload.mutationStatus.cellIndex,
      } as const

      return {
        ...state,
        mutationCellsStatus:
          action.payload.mutationStatus.pasteIndex === 0
            ? [element]
            : state.mutationCellsStatus.concat([element]),
      }
    }
    case CURRENT_DATA_ACTION_TYPES.SET: {
      return {
        ...state,
        currentData: action.payload.currentData,
      }
    }
    case CONTEXT_MENU_COMMAND_PREVIEW_ACTION_TYPES.SET_PREVIEW: {
      if (!state.clipboardInfo) {
        return { ...state }
      }
      const clipboardParseResult = parseExcelData(
        state.clipboardInfo.original,
        {
          validate: state.dataValidationType,
        }
      )

      const pasteData = clipboardParseResult.data.map(
        (item) => item.validatedValue
      )
      return {
        ...state,
        contextMenuActionPreview: {
          pasteAll: pasteData.join(`, `),
          pastePreserveEmpty: pasteData
            .map((item, idx) =>
              item !== ``
                ? item
                : (action.payload.currentData[
                    state.contextMenuTriggerCellIndex + idx
                  ]?.[action.payload.mutateDataKey] ?? ``)
            )
            .join(`, `),
          pasteSkipEmpty: pasteData.filter(Boolean).join(`, `),
        },
      }
    }
    case MENU_STATE_ACTION_TYPES.SET_CONTEXT_MENU_OPEN: {
      const nextOpen = action.payload?.isOpen ?? false
      return {
        ...state,
        isContextMenuOpen: nextOpen,
        contextMenuTriggerCellIndex: nextOpen
          ? action.payload?.triggerCellIndex
          : -1,
      }
    }
    case PASTE_FEEDBACK_ACTION_TYPES.PASTE_ERRORED:
    case PASTE_FEEDBACK_ACTION_TYPES.PASTE_SUCCESS: {
      const { payload } = action as PasteFeedbackActionAction<
        DataItem,
        typeof PASTE_FEEDBACK_ACTION_TYPES.PASTE_SUCCESS
      >
      return {
        ...state,
        upsertData: payload.upsertData,
        prevData: payload.prevData,
        pendingErroredParsedClipboardInputState: null,
        isContextMenuOpen: false,
        isFeedbackDialogOpen: false,
        contextMenuTriggerCellIndex: -1,
        contextMenuActionPreview: {
          pasteAll: ``,
          pastePreserveEmpty: ``,
          pasteSkipEmpty: ``,
        },
      }
    }
    case PASTE_FEEDBACK_ACTION_TYPES.UNDO_PASTE_SUCCESS:
    case PASTE_FEEDBACK_ACTION_TYPES.PASTE_CANCEL:
    case CELL_ACTION_TYPES.SINGLE_CLICK:
    case CELL_ACTION_TYPES.OUTER_CLICK:
    case PARENT_ROW_ACTION_TYPES.OUTER_CLICK:
      return {
        ...state,
        mutationCellsStatus: [],
        clipboardInfo: null,
        upsertData: [],
        prevData: null,
        pendingErroredParsedClipboardInputState: null,
        isContextMenuOpen: false,
        isFeedbackDialogOpen: false,
        contextMenuTriggerCellIndex: -1,
        contextMenuActionPreview: {
          pasteAll: ``,
          pastePreserveEmpty: ``,
          pasteSkipEmpty: ``,
        },
      }
    case INVALID_PENDING_PASTE_ACTION_TYPES.SET_PENDING_PASTE_ERROR:
      return {
        ...state,
        isContextMenuOpen: true,
        isFeedbackDialogOpen: true,
        contextMenuTriggerCellIndex: action.payload.triggerCellIndex,
        pendingErroredParsedClipboardInputState: {
          triggerCellIndex: action.payload.triggerCellIndex,
          cps: action.payload.cps,
          errorCode: action.payload.errorCode,
        },
      }
    case CLIPBOARD_STATE_ACTION_TYPES.UPDATE_CLIPBOARD_INFO:
      return {
        ...state,
        clipboardInfo: { ...action.payload },
      }
    default:
      return { ...state }
  }
}

export const TableActionableRowProvider = <DataItem extends object>({
  children,
  currentData,
  mutateDataKey,
  rowUpdateMutationArgs: { mutationItemFn, onSuccess, onMutate, mutationKey },
  dataValidationType = `numeric`,
  logActions = false,
}: {
  logActions?: boolean
  dataValidationType?: ClipboardParseValidationType
  children: ReactNode
  currentData: DataItem[]
  mutateDataKey: NestedKeyOf<DataItem>
  rowUpdateMutationArgs: Pick<
    Parameters<typeof useMutation>[0],
    "mutationKey" | "onSuccess" | "onMutate"
  > & {
    mutationItemFn: (
      updateDataItem: DataItem & { itemIndex: number },
      extra: {
        rowType: StandardInputType
      }
    ) => Promise<unknown>
  }
}) => {
  const { rowType } = use(RowWithInputTypeContext) ?? {
    rowType: `absolute`,
  }
  /**
   * Getting query period because of query period "all" forecast cells
   * closing context menu as soon as the user attempts hovering
   * bug should be fixed by new changes in onInteractOutside context menu
   * keeping this here for reference
   */
  const {
    state: { queryPeriod },
  } = use(PageContext) ?? {
    state: {
      queryPeriod: `historical`,
    },
  }
  const dataLength = currentData.length
  const dispatchRef = useRef<
    (
      action: ReducerActions<DataItem> | ThunkLikeActions
    ) => Promise<void> | void
  >(async () => {})
  const { mutate } = useMutation({
    mutationFn: async (args: {
      upsertData: (DataItem & { itemIndex: number })[]
      prevData: DataItem[] | null
    }): Promise<{
      status: "ok" | "with-errors"
    }> => {
      let hasErrors = false

      // Original sequential processing - commented out due to performance issues
      // Promise.all gives me incorrect changes when refetching data
      // most of the times on a multiple paste first cell is not filled
      // with expected value even though it is correct in the POST request
      /*
      // eslint-disable-next-line no-restricted-syntax
      for (const [index, item] of Object.entries(args.upsertData)) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await mutationItemFn(item, {
            rowType,
          })
          dispatchRef.current({
            type: CELL_FEEDBACK_STATE_ACTION_TYPES.SET_SUCCESS_FEEDBACK,
            payload: {
              mutationStatus: {
                cellIndex: item.itemIndex,
                pasteIndex: +index,
                message: `Cell updated`,
              },
            },
          })
        } catch (_) {
          hasErrors = true
          dispatchRef.current({
            type: CELL_FEEDBACK_STATE_ACTION_TYPES.SET_ERROR_FEEDBACK,
            payload: {
              mutationStatus: {
                cellIndex: item.itemIndex,
                pasteIndex: +index,
                message: `Error while updating cell`,
              },
            },
          })
        }
      }
      */

      // New implementation using Promise.allSettled for better performance
      const promises = args.upsertData.map((item, index) =>
        mutationItemFn(item, {
          rowType,
        })
          .then(() => ({ item, index, success: true }))
          .catch(() => ({ item, index, success: false }))
      )

      // Split promises into batches of 10 for better performance
      const batchSize = 10
      const batchedPromises = []

      for (let i = 0; i < promises.length; i += batchSize) {
        const batch = promises.slice(i, i + batchSize)
        batchedPromises.push(Promise.all(batch))
      }

      // Wait for all batches to complete
      const batchResults = await Promise.all(batchedPromises)

      // Flatten batch results back into single array
      const results = batchResults.flat()

      // Process results and dispatch feedback
      results.forEach((result) => {
        if (result.success) {
          const { item, index } = result
          dispatchRef.current({
            type: CELL_FEEDBACK_STATE_ACTION_TYPES.SET_SUCCESS_FEEDBACK,
            payload: {
              mutationStatus: {
                cellIndex: item.itemIndex,
                pasteIndex: index,
                message: `Cell updated`,
              },
            },
          })
        } else {
          hasErrors = true
          const { item, index } = result
          dispatchRef.current({
            type: CELL_FEEDBACK_STATE_ACTION_TYPES.SET_ERROR_FEEDBACK,
            payload: {
              mutationStatus: {
                cellIndex: item.itemIndex,
                pasteIndex: index,
                message: `Error while updating cell`,
              },
            },
          })
        }
      })

      return Promise.resolve({
        status: hasErrors ? `with-errors` : `ok`,
      })
    },
    mutationKey,
    onSuccess,
    onMutate,
  })

  const midd: (
    f: StateCreator<RowState<DataItem>, [], []>,
    name?: string
  ) => StateCreator<RowState<DataItem>, [], []> = (f) => (set, get, store) => {
    const middlewaredDispatch = (
      action: ReducerActions<DataItem> | ThunkLikeActions
    ) => {
      if (logActions) {
        console.log(`dispatched action`, action)
      }
      const storeDispatch = get().dispatch
      if (typeof action === `function`) {
        const thunkAction = action as unknown as ThunkLikeAction<DataItem>
        return thunkAction({
          dispatch: middlewaredDispatch,
          getState: get,
          mutateFn: mutate,
          mutateDataKey,
        })
      }
      return (
        storeDispatch as (
          _: ReducerActions<DataItem> | ThunkLikeActionCreators
        ) => void
      )(action)
    }
    const { getState } = store

    store.getState = () => {
      const state = getState()
      dispatchRef.current = middlewaredDispatch
      return {
        ...state,
        dispatch: middlewaredDispatch,
      }
    }

    return f(set, get, store)
  }

  const actionableRowStore = useRef(
    createStore<RowState<DataItem>>()(
      midd((set) => ({
        dataValidationType,
        currentData,
        mutationCellsStatus: [],
        clipboardInfo: null,
        pendingErroredParsedClipboardInputState: null,
        isContextMenuOpen: false,
        isFeedbackDialogOpen: false,
        contextMenuTriggerCellIndex: -1,
        upsertData: [],
        contextMenuActionPreview: {
          pasteAll: ``,
          pastePreserveEmpty: ``,
          pasteSkipEmpty: ``,
        },
        prevData: null,
        dispatch: (action: ReducerActions<DataItem> | ThunkLikeAction) =>
          set((state) =>
            reducer<DataItem>(state, action as ReducerActions<DataItem>)
          ),
      }))
    )
  ).current

  useEffect(() => {
    const { dispatch } = actionableRowStore.getState()
    dispatch({
      type: CURRENT_DATA_ACTION_TYPES.SET,
      payload: { currentData },
    })
  }, [actionableRowStore, currentData])

  const contextValue = useMemo(
    () => ({
      store: actionableRowStore,
      mutationKey,
      dataLength,
      rowType,
      queryPeriod,
    }),
    [actionableRowStore, mutationKey, dataLength, rowType, queryPeriod]
  )

  const ContextComponent =
    ActionableRowContext as Context<ActionableRowContextType<DataItem> | null>
  return (
    <ContextComponent.Provider value={contextValue}>
      {children}
    </ContextComponent.Provider>
  )
}

export type ActionableRowProviderProps<T extends object> = Omit<
  ComponentProps<typeof TableActionableRowProvider<T>>,
  "children"
>

const useActionableRowContext = <DataItem extends object>() => {
  const contextValue = use(ActionableRowContext)
  if (!contextValue) throw new Error(`ActionableRowContext not found`)
  return contextValue as unknown as ActionableRowContextType<DataItem>
}

const useActionableRowStore = <DataItem extends object>() => {
  const contextValue = useActionableRowContext<DataItem>()
  const { store } = contextValue
  return store
}

export const ActionableRowFeedbackDialog = <DataItem extends object>({
  children,
}: PropsWithChildren) => {
  const store = useActionableRowStore<DataItem>()
  const dispatch = useStore(store, (state) => state.dispatch)
  const isPasteFeedbackDialogOpen = useStore(
    store,
    (state) => state.isFeedbackDialogOpen
  )
  return (
    <Dialog
      open={isPasteFeedbackDialogOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          dispatch({
            type: PASTE_FEEDBACK_ACTION_TYPES.PASTE_CANCEL,
            payload: {},
          })
        }
      }}
    >
      {children}
      <ActionableRowFeedbackDialogContent />
    </Dialog>
  )
}

type ActionableRowCellProps = {
  cellIndex: number
  context?: boolean
} & (
  | {
      context: true
      cell_period: string
    }
  | {
      context?: false
      cell_period?: string
    }
) &
  ComponentProps<typeof TableCell>

/**
 * @deprecated use ActionablePeriodRowCell instead
 */
export const ActionableRowCell = <DataItem extends object>({
  ref,
  children,
  className,
  cellIndex,
  onMouseEnter,
  context = false,
  cell_period,
  ...cellProps
}: ActionableRowCellProps) => {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const contextMenuRef = useRef<HTMLDivElement>(null)
  const { mutationKey, store, rowType } = useActionableRowContext<DataItem>()
  const dispatch = useStore(store, (state) => state.dispatch)
  const isPasteFeedbackDialogOpen = useStore(
    store,
    (state) => state.isFeedbackDialogOpen
  )
  const isContextMenuOpen = useStore(store, (state) => state.isContextMenuOpen)
  const contextMenuTriggerCellIndex = useStore(
    store,
    (state) => state.contextMenuTriggerCellIndex
  )
  const undoData = useStore(store, (state) => state.prevData)
  const patchedCells = useStore(store, (state) => state.upsertData)
  const mutationCellsStatus = useStore(
    store,
    (state) => state.mutationCellsStatus
  )
  useOnClickOutside(cellRef as RefObject<HTMLTableCellElement>, (e) => {
    if (!isContextMenuOpen && !isPasteFeedbackDialogOpen) {
      const target = e.target as HTMLElement
      // Find the closest <tr> to the clicked element
      const clickedRow = target.closest(`tr`)
      // not in a tr so for sure click must trigger deselection
      if (!clickedRow) {
        dispatch({
          type: PARENT_ROW_ACTION_TYPES.OUTER_CLICK,
          payload: {
            cellIndex,
          },
        })
        return
      }
      const currentCellRow = cellRef.current?.closest(`tr`)
      if (!currentCellRow) {
        throw new Error(`This should be inside a table tr element`)
      }
      // Click outside the cell parent row so we kill selection
      if (clickedRow !== currentCellRow) {
        dispatch({
          type: PARENT_ROW_ACTION_TYPES.OUTER_CLICK,
          payload: {
            cellIndex,
          },
        })
      }
    }
  })
  const lastCheckedClipboardInfo = useStore(
    store,
    (state) => state.clipboardInfo
  )

  const contextMenuActionPreview = useStore(
    store,
    (state) => state.contextMenuActionPreview
  )
  const isMutatingRow = useIsMutating({ mutationKey })

  const isCellActiveByContextMenuSelection =
    lastCheckedClipboardInfo &&
    isContextMenuOpen &&
    !undoData &&
    cellIndex >= contextMenuTriggerCellIndex &&
    cellIndex < contextMenuTriggerCellIndex + lastCheckedClipboardInfo.count

  const isFirstCellActiveByContextMenuSelection =
    lastCheckedClipboardInfo &&
    isContextMenuOpen &&
    !undoData &&
    cellIndex === contextMenuTriggerCellIndex

  const isCellPatched =
    !!undoData &&
    !!patchedCells.find((patchedCell) => patchedCell.itemIndex === cellIndex)

  const isCellFirstPatched =
    !!undoData && patchedCells[0]?.itemIndex === cellIndex

  const hasLatestClipboardPasteableData = !!(
    lastCheckedClipboardInfo && lastCheckedClipboardInfo.count > 0
  )

  const latestClipboardPasteableDataLength = lastCheckedClipboardInfo
    ? lastCheckedClipboardInfo.count
    : 0

  const hasLatestClipboardInfoEmptyValues = !!(
    lastCheckedClipboardInfo &&
    lastCheckedClipboardInfo.count > 0 &&
    lastCheckedClipboardInfo.data.some((val) => val === ``)
  )

  const latestClipboardInfoEmptyValuesLength =
    lastCheckedClipboardInfo?.data.filter((val) => val === ``).length ?? 0

  const mutationCellStatus = mutationCellsStatus.find(
    (item) => item.cellIndex === cellIndex
  )
  const isDisabled = rowType === `mix`

  const TableCellComponent = context ? PeriodTableCell : TableCell
  return (
    <ContextMenu onOpenChange={() => {}}>
      <ContextMenuTrigger asChild>
        <TableCellComponent
          cell_period={cell_period!}
          ref={cellRef}
          onMouseEnter={(e) => {
            if (window.document.hasFocus()) {
              dispatch(checkClipboardInput({}))
            }
            onMouseEnter?.(e)
          }}
          onClick={() => {
            dispatch({
              type: CELL_ACTION_TYPES.SINGLE_CLICK,
              payload: {
                cellIndex,
              },
            })
          }}
          onContextMenu={(e) => {
            if (!window.document.hasFocus()) {
              e.preventDefault()
              return
            }

            dispatch(
              openContextMenu({
                triggerCellIndex: cellIndex,
              })
            )
          }}
          className={cn(
            `relative`,
            isMutatingRow && `pointer-events-none opacity-50`,
            className
          )}
          {...cellProps}
        >
          {children}
          {isCellActiveByContextMenuSelection || isCellPatched ? (
            <div
              className={cn(
                `absolute top-0 left-0 right-0 bottom-0 pointer-events-none z-20`,
                (isCellActiveByContextMenuSelection || isCellPatched) &&
                  `ring-1 ring-inset ring-blue-600`,
                (isFirstCellActiveByContextMenuSelection ||
                  isCellFirstPatched) &&
                  `ring`,
                mutationCellStatus?.status === `error` &&
                  `ring ring-inset ring-red-600`
              )}
            />
          ) : null}
        </TableCellComponent>
      </ContextMenuTrigger>
      <ContextMenuContent
        // aria-disabled={isDisabled}
        ref={contextMenuRef}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (isPasteFeedbackDialogOpen) return
          if (isContextMenuOpen) {
            // event must be a click to close the context menu
            if (e.type.includes(`pointer`)) {
              const target = e.target as HTMLElement
              // Find the closest <tr> to the clicked element
              const clickedRow = target.closest(`tr`)
              // not in a tr so for sure click must trigger deselection
              if (!clickedRow) {
                dispatch({
                  type: PARENT_ROW_ACTION_TYPES.OUTER_CLICK,
                  payload: {
                    cellIndex,
                  },
                })
                return
              }
              const currentCellRow = cellRef.current?.closest(`tr`)
              if (!currentCellRow) {
                throw new Error(`This should be inside a table tr element`)
              }
              // Click outside the cell parent row so we kill selection
              if (clickedRow !== currentCellRow) {
                dispatch({
                  type: PARENT_ROW_ACTION_TYPES.OUTER_CLICK,
                  payload: {
                    cellIndex,
                  },
                })
              }
            }
          }
        }}
      >
        <Show
          when={!isDisabled}
          fallback={
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <ContextMenuItem
                    disabled={isDisabled}
                    title={contextMenuActionPreview.pasteAll}
                  >
                    <span className={`text-gray-400`}>
                      Paste{` `}
                      {latestClipboardPasteableDataLength} Cell
                      {latestClipboardPasteableDataLength > 1 ? `s` : ``}
                      {` `}
                      {hasLatestClipboardInfoEmptyValues &&
                        `(with ${latestClipboardInfoEmptyValuesLength} empty value ${latestClipboardInfoEmptyValuesLength > 1 ? `s` : ``})`}
                    </span>
                  </ContextMenuItem>
                </TooltipTrigger>
                <TooltipContent className={`text-sm max-w-[300px]`}>
                  <p>
                    Copy and paste is not avaible for {rowType} modeling
                    approach, please select a different modeling approach.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
        >
          {hasLatestClipboardPasteableData && (
            <ContextMenuItem
              disabled={isDisabled}
              title={contextMenuActionPreview.pasteAll}
              onClick={async () => {
                dispatch(
                  tryPasteClipboardInput({
                    type: PASTE_ACTION_TYPES.PASTE_ALL,
                    triggerCellIndex: cellIndex,
                  })
                )
              }}
            >
              Paste{` `}
              {latestClipboardPasteableDataLength} Cell
              {latestClipboardPasteableDataLength > 1 ? `s` : ``}
              {` `}
              {hasLatestClipboardInfoEmptyValues &&
                `(with ${latestClipboardInfoEmptyValuesLength} empty value ${latestClipboardInfoEmptyValuesLength > 1 ? `s` : ``})`}
            </ContextMenuItem>
          )}
          {hasLatestClipboardPasteableData &&
            hasLatestClipboardInfoEmptyValues && (
              <>
                <ContextMenuItem
                  disabled={isDisabled}
                  title={contextMenuActionPreview.pastePreserveEmpty}
                  onClick={async () => {
                    dispatch(
                      tryPasteClipboardInput({
                        type: PASTE_ACTION_TYPES.PASTE_PRESERVE_EMPTY,
                        triggerCellIndex: cellIndex,
                      })
                    )
                  }}
                >
                  Paste{` `}
                  {lastCheckedClipboardInfo &&
                    `${lastCheckedClipboardInfo.count} Cell${lastCheckedClipboardInfo.count > 1 ? `s` : ``}`}
                  {` `}
                  {`(preserve current data on ${latestClipboardInfoEmptyValuesLength} empty value ${latestClipboardInfoEmptyValuesLength > 1 ? `s` : ``})`}
                </ContextMenuItem>
                <ContextMenuItem
                  disabled={isDisabled}
                  title={contextMenuActionPreview.pasteSkipEmpty}
                  onClick={async () => {
                    dispatch(
                      tryPasteClipboardInput({
                        type: PASTE_ACTION_TYPES.PASTE_SKIP_EMPTY,
                        triggerCellIndex: cellIndex,
                      })
                    )
                  }}
                >
                  Paste{` `}
                  {lastCheckedClipboardInfo &&
                    `${lastCheckedClipboardInfo.count - latestClipboardInfoEmptyValuesLength} Cell${lastCheckedClipboardInfo.count - latestClipboardInfoEmptyValuesLength > 1 ? `s` : ``}`}
                  {` `}
                  {`(discard ${latestClipboardInfoEmptyValuesLength} empty Cell ${latestClipboardInfoEmptyValuesLength > 1 ? `s` : ``})`}
                </ContextMenuItem>
              </>
            )}
        </Show>
        <Show when={isCellPatched}>
          <ContextMenuItem
            onClick={() => {
              dispatch(undoPaste({}))
            }}
          >
            Undo{` `}
            {`${patchedCells.length} Cell${patchedCells.length > 1 ? `s` : ``}`}
          </ContextMenuItem>
        </Show>
      </ContextMenuContent>
    </ContextMenu>
  )
}

export type ActionablePeriodRowCellProps = {
  cell_period: string
} & ActionableRowCellProps

export const ActionablePeriodRowCell = <DataItem extends object>({
  ref,
  children,
  className,
  cellIndex,
  onMouseEnter,
  cell_period,
  ...cellProps
}: ActionablePeriodRowCellProps) => {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const contextMenuRef = useRef<HTMLDivElement>(null)
  const { mutationKey, store, rowType } = useActionableRowContext<DataItem>()
  const dispatch = useStore(store, (state) => state.dispatch)
  const isPasteFeedbackDialogOpen = useStore(
    store,
    (state) => state.isFeedbackDialogOpen
  )
  const isContextMenuOpen = useStore(store, (state) => state.isContextMenuOpen)
  const contextMenuTriggerCellIndex = useStore(
    store,
    (state) => state.contextMenuTriggerCellIndex
  )
  const undoData = useStore(store, (state) => state.prevData)
  const patchedCells = useStore(store, (state) => state.upsertData)
  const mutationCellsStatus = useStore(
    store,
    (state) => state.mutationCellsStatus
  )
  useOnClickOutside(cellRef as RefObject<HTMLTableCellElement>, (e) => {
    if (!isContextMenuOpen && !isPasteFeedbackDialogOpen) {
      const target = e.target as HTMLElement
      // Find the closest <tr> to the clicked element
      const clickedRow = target.closest(`tr`)
      // not in a tr so for sure click must trigger deselection
      if (!clickedRow) {
        dispatch({
          type: PARENT_ROW_ACTION_TYPES.OUTER_CLICK,
          payload: {
            cellIndex,
          },
        })
        return
      }
      const currentCellRow = cellRef.current?.closest(`tr`)
      if (!currentCellRow) {
        throw new Error(`This should be inside a table tr element`)
      }
      // Click outside the cell parent row so we kill selection
      if (clickedRow !== currentCellRow) {
        dispatch({
          type: PARENT_ROW_ACTION_TYPES.OUTER_CLICK,
          payload: {
            cellIndex,
          },
        })
      }
    }
  })
  const lastCheckedClipboardInfo = useStore(
    store,
    (state) => state.clipboardInfo
  )

  const contextMenuActionPreview = useStore(
    store,
    (state) => state.contextMenuActionPreview
  )
  const isMutatingRow = useIsMutating({ mutationKey })

  const isCellActiveByContextMenuSelection =
    lastCheckedClipboardInfo &&
    isContextMenuOpen &&
    !undoData &&
    cellIndex >= contextMenuTriggerCellIndex &&
    cellIndex < contextMenuTriggerCellIndex + lastCheckedClipboardInfo.count

  const isFirstCellActiveByContextMenuSelection =
    lastCheckedClipboardInfo &&
    isContextMenuOpen &&
    !undoData &&
    cellIndex === contextMenuTriggerCellIndex

  const isCellPatched =
    !!undoData &&
    !!patchedCells.find((patchedCell) => patchedCell.itemIndex === cellIndex)

  const isCellFirstPatched =
    !!undoData && patchedCells[0]?.itemIndex === cellIndex

  const hasLatestClipboardPasteableData = !!(
    lastCheckedClipboardInfo && lastCheckedClipboardInfo.count > 0
  )

  const latestClipboardPasteableDataLength = lastCheckedClipboardInfo
    ? lastCheckedClipboardInfo.count
    : 0

  const hasLatestClipboardInfoEmptyValues = !!(
    lastCheckedClipboardInfo &&
    lastCheckedClipboardInfo.count > 0 &&
    lastCheckedClipboardInfo.data.some((val) => val === ``)
  )

  const latestClipboardInfoEmptyValuesLength =
    lastCheckedClipboardInfo?.data.filter((val) => val === ``).length ?? 0

  const mutationCellStatus = mutationCellsStatus.find(
    (item) => item.cellIndex === cellIndex
  )
  const isDisabled = rowType === `mix`

  return (
    <ContextMenu onOpenChange={() => {}}>
      <ContextMenuTrigger asChild>
        <PeriodTableCell
          cell_period={cell_period}
          ref={cellRef}
          onMouseEnter={(e) => {
            if (window.document.hasFocus()) {
              dispatch(checkClipboardInput({}))
            }
            onMouseEnter?.(e)
          }}
          onClick={() => {
            dispatch({
              type: CELL_ACTION_TYPES.SINGLE_CLICK,
              payload: {
                cellIndex,
              },
            })
          }}
          onContextMenu={(e) => {
            if (!window.document.hasFocus()) {
              e.preventDefault()
              return
            }

            dispatch(
              openContextMenu({
                triggerCellIndex: cellIndex,
              })
            )
          }}
          className={cn(
            `relative`,
            isMutatingRow && `pointer-events-none opacity-50`,
            className
          )}
          {...cellProps}
        >
          {children}
          {isCellActiveByContextMenuSelection || isCellPatched ? (
            <div
              className={cn(
                `absolute top-0 left-0 right-0 bottom-0 pointer-events-none z-20`,
                (isCellActiveByContextMenuSelection || isCellPatched) &&
                  `ring-1 ring-inset ring-blue-600`,
                (isFirstCellActiveByContextMenuSelection ||
                  isCellFirstPatched) &&
                  `ring`,
                mutationCellStatus?.status === `error` &&
                  `ring ring-inset ring-red-600`
              )}
            />
          ) : null}
        </PeriodTableCell>
      </ContextMenuTrigger>
      <ContextMenuContent
        // aria-disabled={isDisabled}
        ref={contextMenuRef}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (isPasteFeedbackDialogOpen) return
          if (isContextMenuOpen) {
            // event must be a click to close the context menu
            if (e.type.includes(`pointer`)) {
              const target = e.target as HTMLElement
              // Find the closest <tr> to the clicked element
              const clickedRow = target.closest(`tr`)
              // not in a tr so for sure click must trigger deselection
              if (!clickedRow) {
                dispatch({
                  type: PARENT_ROW_ACTION_TYPES.OUTER_CLICK,
                  payload: {
                    cellIndex,
                  },
                })
                return
              }
              const currentCellRow = cellRef.current?.closest(`tr`)
              if (!currentCellRow) {
                throw new Error(`This should be inside a table tr element`)
              }
              // Click outside the cell parent row so we kill selection
              if (clickedRow !== currentCellRow) {
                dispatch({
                  type: PARENT_ROW_ACTION_TYPES.OUTER_CLICK,
                  payload: {
                    cellIndex,
                  },
                })
              }
            }
          }
        }}
      >
        <Show
          when={!isDisabled}
          fallback={
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <ContextMenuItem
                    disabled={isDisabled}
                    title={contextMenuActionPreview.pasteAll}
                  >
                    <span className={`text-gray-400`}>
                      Paste{` `}
                      {latestClipboardPasteableDataLength} Cell
                      {latestClipboardPasteableDataLength > 1 ? `s` : ``}
                      {` `}
                      {hasLatestClipboardInfoEmptyValues &&
                        `(with ${latestClipboardInfoEmptyValuesLength} empty value ${latestClipboardInfoEmptyValuesLength > 1 ? `s` : ``})`}
                    </span>
                  </ContextMenuItem>
                </TooltipTrigger>
                <TooltipContent className={`text-sm max-w-[300px]`}>
                  <p>
                    Copy and paste is not avaible for {rowType} modeling
                    approach, please select a different modeling approach.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
        >
          {hasLatestClipboardPasteableData && (
            <ContextMenuItem
              disabled={isDisabled}
              title={contextMenuActionPreview.pasteAll}
              onClick={async () => {
                dispatch(
                  tryPasteClipboardInput({
                    type: PASTE_ACTION_TYPES.PASTE_ALL,
                    triggerCellIndex: cellIndex,
                  })
                )
              }}
            >
              Paste{` `}
              {latestClipboardPasteableDataLength} Cell
              {latestClipboardPasteableDataLength > 1 ? `s` : ``}
              {` `}
              {hasLatestClipboardInfoEmptyValues &&
                `(with ${latestClipboardInfoEmptyValuesLength} empty value ${latestClipboardInfoEmptyValuesLength > 1 ? `s` : ``})`}
            </ContextMenuItem>
          )}
          {hasLatestClipboardPasteableData &&
            hasLatestClipboardInfoEmptyValues && (
              <>
                <ContextMenuItem
                  disabled={isDisabled}
                  title={contextMenuActionPreview.pastePreserveEmpty}
                  onClick={async () => {
                    dispatch(
                      tryPasteClipboardInput({
                        type: PASTE_ACTION_TYPES.PASTE_PRESERVE_EMPTY,
                        triggerCellIndex: cellIndex,
                      })
                    )
                  }}
                >
                  Paste{` `}
                  {lastCheckedClipboardInfo &&
                    `${lastCheckedClipboardInfo.count} Cell${lastCheckedClipboardInfo.count > 1 ? `s` : ``}`}
                  {` `}
                  {`(preserve current data on ${latestClipboardInfoEmptyValuesLength} empty value ${latestClipboardInfoEmptyValuesLength > 1 ? `s` : ``})`}
                </ContextMenuItem>
                <ContextMenuItem
                  disabled={isDisabled}
                  title={contextMenuActionPreview.pasteSkipEmpty}
                  onClick={async () => {
                    dispatch(
                      tryPasteClipboardInput({
                        type: PASTE_ACTION_TYPES.PASTE_SKIP_EMPTY,
                        triggerCellIndex: cellIndex,
                      })
                    )
                  }}
                >
                  Paste{` `}
                  {lastCheckedClipboardInfo &&
                    `${lastCheckedClipboardInfo.count - latestClipboardInfoEmptyValuesLength} Cell${lastCheckedClipboardInfo.count - latestClipboardInfoEmptyValuesLength > 1 ? `s` : ``}`}
                  {` `}
                  {`(discard ${latestClipboardInfoEmptyValuesLength} empty Cell ${latestClipboardInfoEmptyValuesLength > 1 ? `s` : ``})`}
                </ContextMenuItem>
              </>
            )}
        </Show>
        <Show when={isCellPatched}>
          <ContextMenuItem
            onClick={() => {
              dispatch(undoPaste({}))
            }}
          >
            Undo{` `}
            {`${patchedCells.length} Cell${patchedCells.length > 1 ? `s` : ``}`}
          </ContextMenuItem>
        </Show>
      </ContextMenuContent>
    </ContextMenu>
  )
}

export const ActionableRowFeedbackDialogContent = <
  DataItem extends object,
>() => {
  const { t: tCommon } = useTranslation(`common`)
  const { mutationKey, dataLength, store } = useActionableRowContext<DataItem>()
  const dispatch = useStore(store, (state) => state.dispatch)
  const pendingErroredParsedClipboardInputState = useStore(
    store,
    (state) => state.pendingErroredParsedClipboardInputState
  )
  const isMutatingRow = useIsMutating({ mutationKey }) > 0
  return (
    <DialogContent className={`flex flex-col min-w-[710px] p-0`}>
      <div className={`p-8`}>
        {pendingErroredParsedClipboardInputState && (
          <>
            <DialogTitle
              className={`font-medium text-[28px] leading-[39px] mb-2`}
            >
              {pendingErroredParsedClipboardInputState.errorCode ===
                PENDING_PASTE_ERROR.LIMIT_OVERFLOW && <>Paste within limit?</>}
              {pendingErroredParsedClipboardInputState.errorCode ===
                PENDING_PASTE_ERROR.INVALID_CHARS && (
                <>Invalid characters detected</>
              )}
            </DialogTitle>
            <DialogDescription
              className={`font-normal text-base leading-[22.4px] text-secondary`}
            >
              {pendingErroredParsedClipboardInputState.errorCode ===
                PENDING_PASTE_ERROR.LIMIT_OVERFLOW && (
                <>
                  You are trying to paste{` `}
                  <span className={`font-semibold`}>
                    {pendingErroredParsedClipboardInputState.cps.data.length}
                    cells, but the row only supports{` `}
                    {dataLength -
                      pendingErroredParsedClipboardInputState.triggerCellIndex}
                  </span>
                  . Paste the{` `}
                  {dataLength -
                    pendingErroredParsedClipboardInputState.triggerCellIndex}
                  {` `}
                  available cells or cancel?
                </>
              )}
              {pendingErroredParsedClipboardInputState.errorCode ===
                PENDING_PASTE_ERROR.INVALID_CHARS && (
                <>
                  Some cells contain invalid characters (
                  {pendingErroredParsedClipboardInputState.cps.errors
                    .map((cp, index) => `${index + 1}: ${cp.itemValue}`)
                    .join(`, `)}
                  ). Proceed with pasting only the valid ones?
                </>
              )}
            </DialogDescription>
          </>
        )}
      </div>
      <DialogFooter className={cn(`p-6 flex justify-end`)}>
        <Button
          size={`md`}
          variant={`secondary`}
          className={`min-w-[120px]`}
          onClick={() => {
            dispatch({
              type: PASTE_FEEDBACK_ACTION_TYPES.PASTE_CANCEL,
              payload: {},
            })
          }}
        >
          {tCommon(`buttons.back`)}
        </Button>
        <Button
          size={`md`}
          variant={`primary`}
          className={`min-w-[120px]`}
          onClick={async () => {
            dispatch(pasteErroredClipboardInput({}))
          }}
          disabled={isMutatingRow}
        >
          {tCommon(`buttons.paste`)}
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
